import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const SCOPE_COLORS = { 1: 'var(--neon-orange)', 2: 'var(--neon-yellow)', 3: 'var(--neon-cyan)' };

export default function EmissionsLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/emissions/my')
      .then((r) => setLogs(r.data.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this emission log entry? Your carbon score will be recalculated.')) return;
    setDeleting(id);
    try {
      await api.delete(`/emissions/log/${id}`);
      load();
    } catch {}
    setDeleting(null);
  };

  const filtered = logs.filter((l) => {
    if (scopeFilter !== 'all' && l.scope !== parseInt(scopeFilter, 10)) return false;
    if (search) {
      const s = search.toLowerCase();
      return (l.label || l.activity || '').toLowerCase().includes(s) ||
        (l.ghg_category || '').toLowerCase().includes(s) ||
        (l.site || '').toLowerCase().includes(s);
    }
    return true;
  });

  const exportCSV = () => {
    const header = 'Period,Scope,GHG Category,Activity,Site,Quantity,Unit,tCO2e,Date';
    const rows = filtered.map((l) => [
      l.period, l.scope, l.ghg_category, l.label || l.activity, l.site || '', l.quantity, l.unit,
      (l.total_kgco2e / 1000).toFixed(3), new Date(l.created_at).toLocaleDateString('en-IN'),
    ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'emission_log.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Emission Log History</div>
        <h1>All logged emission activities</h1>
        <p className="section-copy">Full audit trail of every emission entry. Delete to recalculate your carbon score.</p>
      </section>

      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {['all', '1', '2', '3'].map((s) => (
              <button key={s} type="button"
                className={`btn btn-sm ${scopeFilter === s ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setScopeFilter(s)} style={{ padding: '0.25rem 0.75rem' }}>
                {s === 'all' ? 'All scopes' : `Scope ${s}`}
              </button>
            ))}
            <input className="form-input" type="text" placeholder="Search activity, category…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', maxWidth: '220px' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={exportCSV}>Download CSV</button>
            <Link to="/emissions" className="btn btn-primary btn-sm">Log new activity</Link>
          </div>
        </div>

        {loading ? (
          <div className="stat-sub">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ background: 'rgba(255,255,255,0.02)', padding: '2rem', textAlign: 'center' }}>
            <div className="stat-sub">No emission logs found. <Link to="/emissions" style={{ color: 'var(--neon-cyan)' }}>Log your first activity →</Link></div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Scope</th>
                <th>GHG Category</th>
                <th>Activity</th>
                <th>Site</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>tCO2e</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>{l.period}</td>
                  <td>
                    <span className="badge" style={{ color: SCOPE_COLORS[l.scope], borderColor: SCOPE_COLORS[l.scope], fontSize: '0.6rem' }}>
                      Scope {l.scope}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--muted2)' }}>{l.ghg_category}</td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{l.label || l.activity}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--muted2)' }}>{l.site || '—'}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>{l.quantity?.toLocaleString('en-IN')}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>{l.unit}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', color: 'var(--neon-orange)' }}>
                    {(l.total_kgco2e / 1000).toFixed(3)}
                  </td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--muted2)' }}>
                    {new Date(l.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td>
                    <button type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: '0.65rem', color: 'var(--neon-pink)', borderColor: 'var(--neon-pink)', padding: '0.2rem 0.5rem' }}
                      onClick={() => handleDelete(l.id)}
                      disabled={deleting === l.id}>
                      {deleting === l.id ? '…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: '1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--muted2)' }}>
          Showing {filtered.length} of {logs.length} entries
        </div>
      </section>
    </div>
  );
}
